import {
  MultiSelectorField,
  ProductTypeEnum,
  SelectorField,
  getCurrencyPairFromSecurity,
  type CurrencySelectItem,
  type Security,
  type StringSelectItem,
} from '@talos/kyoko';
import { immerable } from 'immer';
import type { OMSReferenceDataState } from '../../types';
import {
  getExchangeAndTypeFromSecurity,
  getSettlementTypeFromExchangeAndType,
  getShortExchangeName,
  isSecurityExchangeAndTypeMatch,
} from './utils';

interface CFDData {
  exchangeAndTypeField: SelectorField<StringSelectItem>;
  symbolField: SelectorField<CurrencySelectItem>;
  marketAccountField: MultiSelectorField<StringSelectItem>;
}

const getCFDSymbolSelectItem = (security: Security): CurrencySelectItem => {
  const ccyPair = getCurrencyPairFromSecurity(security);

  return {
    value: ccyPair,
    label: ccyPair,
    description: security.Description,
    baseCurrency: security.BaseCurrency ?? ccyPair?.split('-')[0],
    quoteCurrency: security.QuoteCurrency ?? ccyPair?.split('-')[1],
  };
};

export class CFD {
  [immerable] = true;

  public static readonly type = ProductTypeEnum.CFD;
  private readonly _data: CFDData;

  private constructor(
    private _referenceData: OMSReferenceDataState,
    data: CFDData
  ) {
    this._data = data;
  }

  public static createFromBlank(referenceData: OMSReferenceDataState) {
    const uniqueCcyPairs = CFD.getAvailableSymbols(referenceData);
    const uniqueExchangeAndTypes = CFD.getAvailableExchanges(referenceData);

    const data = {
      exchangeAndTypeField: new SelectorField<StringSelectItem>({
        name: 'Exchange & Type',
        idProperty: 'value',
        availableItems: uniqueExchangeAndTypes,
      }),
      symbolField: new SelectorField<CurrencySelectItem>({
        name: 'Symbol',
        idProperty: 'value',
        availableItems: uniqueCcyPairs,
      }),
      marketAccountField: new MultiSelectorField<StringSelectItem>({
        name: 'Market Account',
        idProperty: 'value',
      }),
    };

    return new CFD(referenceData, data);
  }

  public static createFromSecurity(referenceData: OMSReferenceDataState, security: Security): CFD {
    const market = security?.Markets?.[0];
    const type = security?.SettleValueType;

    if (!market || !type) {
      throw new Error(`${security.Symbol} is not a CFD`);
    }

    const exchangeAndType = getExchangeAndTypeFromSecurity(security, referenceData.markets.marketsByName);

    const symbol = getCurrencyPairFromSecurity(security);
    const availableSymbols = CFD.getSymbolsForExchange(referenceData, exchangeAndType);
    const availableExchanges: StringSelectItem[] = CFD.getExchangesForSymbol(referenceData, symbol);

    const data = {
      exchangeAndTypeField: new SelectorField({
        name: 'Exchange & Type',
        idProperty: 'value',
        value: availableExchanges.find(ex => ex.value === exchangeAndType),
        availableItems: availableExchanges,
      }),
      symbolField: new SelectorField({
        name: 'Symbol',
        idProperty: 'value',
        value: availableSymbols.find(c => c.value === symbol),
        availableItems: availableSymbols,
      }),
      marketAccountField: new MultiSelectorField<StringSelectItem>({
        name: 'Market Account',
        idProperty: 'value',
      }),
    };

    return new CFD(referenceData, data);
  }

  public get data(): CFDData {
    return this._data;
  }

  public get security(): Security | undefined {
    const { symbolField, exchangeAndTypeField } = this.data;
    if (!symbolField.hasValue || !exchangeAndTypeField.hasValue) {
      return undefined;
    }
    const [BaseCurrency, QuoteCurrency] = symbolField.value!.value.split('-');

    const type = getSettlementTypeFromExchangeAndType(exchangeAndTypeField.value?.value);

    return this._referenceData.securities.cfds.find(cfd => {
      const marketShortName = getShortExchangeName(cfd, this._referenceData.markets.marketsByName);

      return (
        cfd.BaseCurrency === BaseCurrency &&
        cfd.QuoteCurrency === QuoteCurrency &&
        exchangeAndTypeField.value!.value.includes(marketShortName) &&
        cfd.SettleValueType === type
      );
    });
  }

  public updateExchangeAndType = (exchangeAndType: StringSelectItem | undefined) => {
    const newData = {
      ...this._data,
      exchangeAndTypeField: this._data.exchangeAndTypeField.updateValue(exchangeAndType),
      symbolField: this._data.symbolField.updateAvailableItems(
        exchangeAndType
          ? CFD.getSymbolsForExchange(this._referenceData, exchangeAndType.value)
          : CFD.getAvailableSymbols(this._referenceData)
      ),
    };
    return this.updateData(newData);
  };

  public updateSymbol = (symbol: CurrencySelectItem | undefined) => {
    const newData = {
      ...this._data,
      symbolField: this._data.symbolField.updateValue(symbol),
      exchangeAndTypeField: this._data.exchangeAndTypeField.updateAvailableItems(
        symbol
          ? CFD.getExchangesForSymbol(this._referenceData, symbol.value)
          : CFD.getAvailableExchanges(this._referenceData)
      ),
    };
    return this.updateData(newData);
  };

  public updateMarketAccount = (marketAccounts: StringSelectItem[] | undefined) => {
    const newData = {
      ...this._data,
      marketAccountField: this._data.marketAccountField.updateValue(marketAccounts),
    };
    return this.updateData(newData);
  };

  public getType(): ProductTypeEnum {
    return CFD.type;
  }

  private updateData(data: Partial<CFDData>): CFD {
    const newData = {
      ...this._data,
      ...data,
    };
    return new CFD(this._referenceData, newData);
  }

  public static getAvailableSymbols(referenceData: OMSReferenceDataState): CurrencySelectItem[] {
    const cfdSymbols = referenceData.securities.cfds.reduce<Map<string, CurrencySelectItem>>((acc, cfd) => {
      const symbolSelectItem = getCFDSymbolSelectItem(cfd);
      if (!acc.has(symbolSelectItem.value)) {
        acc.set(symbolSelectItem.value, symbolSelectItem);
      }
      return acc;
    }, new Map());

    return Array.from(cfdSymbols.values());
  }

  private static getAvailableExchanges(referenceData: OMSReferenceDataState): StringSelectItem[] {
    const cfds = referenceData.securities.cfds;
    const markets = cfds.map(cfd => getExchangeAndTypeFromSecurity(cfd, referenceData.markets.marketsByName));

    return Array.from(new Set(markets)).map(ex => ({
      label: ex.split('&')[0],
      value: ex,
    }));
  }

  private static getExchangesForSymbol(referenceData: OMSReferenceDataState, symbol: string): StringSelectItem[] {
    if (!symbol) {
      return CFD.getAvailableExchanges(referenceData);
    }

    const cfds = referenceData.securities.cfds.filter(cfd => getCurrencyPairFromSecurity(cfd) === symbol);
    const markets = cfds.map(cfd => getExchangeAndTypeFromSecurity(cfd, referenceData.markets.marketsByName));

    return Array.from(new Set(markets)).map(ex => ({
      label: ex.split('&')[0],
      value: ex,
    }));
  }

  public static getSymbolsForExchange(
    referenceData: OMSReferenceDataState,
    exchangeAndType: string
  ): CurrencySelectItem[] {
    if (!exchangeAndType) {
      return CFD.getAvailableSymbols(referenceData);
    }
    const cfds = referenceData.securities.cfds;
    const symbols = cfds
      .filter(c => isSecurityExchangeAndTypeMatch(c, exchangeAndType, referenceData.markets.marketsByName))
      .reduce<Map<string, CurrencySelectItem>>((acc, cfd) => {
        const symbolSelectItem = getCFDSymbolSelectItem(cfd);
        if (!acc.has(symbolSelectItem.value)) {
          acc.set(symbolSelectItem.value, symbolSelectItem);
        }
        return acc;
      }, new Map());

    return Array.from(symbols.values());
  }
}
